import clsx from "clsx";
import type { ComponentProps, ReactNode } from "react";
import { useCallback, useState } from "react";
import { WithChat } from "~/components/common/chat/WithChat";
import { ChatContextProvider } from "~/context/ChatContext";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import { useMessages as useVendorMessages } from "~/hooks/vendors/deals/useMessages";
import { useSessions } from "~/hooks/vendors/useSessions";
import type { Deal } from "~/models/Deal";
import { getVendorMessageReceiver } from "~/models/Message";
import Styles from "./DealLayout.module.scss";
import { DealLayoutHeaderVendor } from "./DealLayoutHeaderVendor";

type Props = {
	children: ReactNode;
	deal: Deal;
	enableChat?: boolean;
	items: ComponentProps<typeof DealLayoutHeaderVendor>["items"];
	noBackground?: boolean;
	useSimpleSpHeader?: boolean;
};

export function VendorDealLayout({
	deal,
	children,
	items,
	noBackground = false,
	useSimpleSpHeader = false,
	enableChat = false
}: Props) {
	const { data: session } = useSessions().index();
	const { data: messages } = useVendorMessages().index(deal.id);
	const { match: isSingleColumn } = useMediaQuery(MEDIA_QUERY.singleColumn);
	const [isOpen, setIsOpen] = useState(false);
	const isChatOpen = !isSingleColumn && isOpen;

	const onToggleChat = useCallback(() => {
		setIsOpen((prev) => !prev);
	}, []);

	if (session === null || messages === null) {
		return null;
	}

	return (
		<div>
			<DealLayoutHeaderVendor
				deal={deal}
				enableChat={enableChat}
				isChatOpen={isChatOpen}
				items={items}
				messages={messages}
				onToggleChat={onToggleChat}
				session={session}
				useSimpleSpHeader={useSimpleSpHeader}
			/>
			<WithChat
				deal={deal}
				enabled={isChatOpen}
				isAdmin={session.admin}
				isOpen={isChatOpen}
				isVendor
				messages={messages}
				onClose={onToggleChat}
				receiver={getVendorMessageReceiver(deal, session)}
			>
				<ChatContextProvider isChatOpen={isChatOpen}>
					<div
						className={clsx(
							Styles.Container,
							noBackground ? Styles.Transparent : Styles.White
						)}
					>
						{children}
					</div>
				</ChatContextProvider>
			</WithChat>
		</div>
	);
}
